import React, { useState, useEffect } from 'react';
import { 
  FiCheck, FiX, FiClock, FiDollarSign, FiTrendingUp, 
  FiAlertCircle, FiZap, FiSmartphone, FiUsers, FiBarChart,
  FiLock, FiRefreshCw, FiDatabase, FiCloud, FiShield,
  FiArrowRight, FiChevronDown, FiChevronUp
} from 'react-icons/fi';
import { Link } from 'react-router-dom';
import heroImage from '../../assets/images/UniquePhysique-Personal-Training-Header1.jpg';
import './FeaturesComparison.css';

const FeaturesComparison = () => {
  const [activeTab, setActiveTab] = useState('features');
  const [expandedFaq, setExpandedFaq] = useState(null);
  const [calculatorValues, setCalculatorValues] = useState({
    clients: 20,
    hoursPerWeek: 10,
    hourlyRate: 75
  });

  // Pain points data
  const painPoints = [
    {
      icon: '😵',
      title: 'Formula Nightmares',
      excel: 'Complex nested IF statements, VLOOKUP errors, broken references',
      coachify: 'Automated calculations with validated inputs and error handling'
    },
    {
      icon: '📱',
      title: 'Mobile Accessibility',
      excel: 'Desktop-only, terrible on phones, clients struggle to view',
      coachify: 'Responsive design works perfectly on all devices'
    },
    {
      icon: '🔄',
      title: 'Version Control',
      excel: 'Multiple file versions, "Final_v2_REAL_FINAL.xlsx" chaos',
      coachify: 'Single source of truth with automatic updates'
    },
    {
      icon: '📊',
      title: 'Data Visualization',
      excel: 'Static charts, manual updates, limited customization',
      coachify: 'Interactive charts with real-time data updates'
    },
    {
      icon: '👥',
      title: 'Client Management',
      excel: 'Separate files for each client, manual tracking',
      coachify: 'Centralized dashboard with all clients in one place'
    },
    {
      icon: '🔐',
      title: 'Security & Privacy',
      excel: 'Files shared via email, no access control',
      coachify: 'Secure login, role-based access, encrypted data'
    }
  ];

  // Feature comparison data
  const features = [
    { 
      category: 'Calculation & Formulas',
      items: [
        { feature: 'BMR/TDEE Calculations', excel: 'Manual formulas', coachify: 'Automated', excelStatus: 'partial', coachifyStatus: 'full' },
        { feature: 'Macro Distribution', excel: 'Complex formulas', coachify: 'One-click', excelStatus: 'partial', coachifyStatus: 'full' },
        { feature: 'Progress Tracking', excel: 'Manual entry', coachify: 'Automated graphs', excelStatus: 'partial', coachifyStatus: 'full' },
        { feature: 'Error Handling', excel: '#REF! #VALUE!', coachify: 'Validated inputs', excelStatus: 'none', coachifyStatus: 'full' }
      ]
    },
    {
      category: 'Client Experience',
      items: [
        { feature: 'Mobile Access', excel: 'Not optimized', coachify: 'Fully responsive', excelStatus: 'none', coachifyStatus: 'full' },
        { feature: 'Real-time Updates', excel: 'Manual refresh', coachify: 'Live sync', excelStatus: 'none', coachifyStatus: 'full' },
        { feature: 'User Interface', excel: 'Spreadsheet grid', coachify: 'Modern UI/UX', excelStatus: 'partial', coachifyStatus: 'full' },
        { feature: 'Client Portal', excel: 'Email attachments', coachify: 'Dedicated login', excelStatus: 'none', coachifyStatus: 'full' }
      ]
    },
    {
      category: 'Business Management',
      items: [
        { feature: 'Multi-client Support', excel: 'Multiple files', coachify: 'Unified dashboard', excelStatus: 'partial', coachifyStatus: 'full' },
        { feature: 'Automated Reporting', excel: 'Manual creation', coachify: 'One-click reports', excelStatus: 'none', coachifyStatus: 'full' },
        { feature: 'Payment Integration', excel: 'External tools', coachify: 'Built-in (coming)', excelStatus: 'none', coachifyStatus: 'partial' },
        { feature: 'Scheduling', excel: 'Separate calendar', coachify: 'Integrated calendar', excelStatus: 'none', coachifyStatus: 'full' }
      ]
    },
    {
      category: 'Data & Security',
      items: [
        { feature: 'Data Backup', excel: 'Manual saves', coachify: 'Auto backup', excelStatus: 'partial', coachifyStatus: 'full' },
        { feature: 'Access Control', excel: 'File passwords', coachify: 'Role-based access', excelStatus: 'partial', coachifyStatus: 'full' },
        { feature: 'Data Encryption', excel: 'Basic protection', coachify: 'Industry standard', excelStatus: 'partial', coachifyStatus: 'full' },
        { feature: 'Audit Trail', excel: 'None', coachify: 'Full history', excelStatus: 'none', coachifyStatus: 'full' }
      ]
    }
  ];

  // Calculate time savings
  const calculateTimeSavings = () => {
    const tasksPerClient = [
      { task: 'Initial setup & calculations', excelTime: 30, coachifyTime: 5 },
      { task: 'Weekly progress updates', excelTime: 15, coachifyTime: 2 },
      { task: 'Macro adjustments', excelTime: 10, coachifyTime: 1 },
      { task: 'Report generation', excelTime: 20, coachifyTime: 2 },
      { task: 'Client communication', excelTime: 15, coachifyTime: 5 }
    ];

    const weeklyExcelTime = tasksPerClient.reduce((sum, task) => sum + task.excelTime, 0);
    const weeklyCoachifyTime = tasksPerClient.reduce((sum, task) => sum + task.coachifyTime, 0);
    const timeSavedPerClient = weeklyExcelTime - weeklyCoachifyTime;
    const totalTimeSaved = timeSavedPerClient * calculatorValues.clients;
    const hoursSavedPerMonth = (totalTimeSaved * 4) / 60;
    const moneySaved = hoursSavedPerMonth * calculatorValues.hourlyRate;

    return {
      tasksPerClient,
      weeklyExcelTime,
      weeklyCoachifyTime,
      timeSavedPerClient,
      totalTimeSaved,
      hoursSavedPerMonth,
      moneySaved
    };
  };

  const savings = calculateTimeSavings();

  // Implementation timeline
  const timeline = [
    { 
      phase: 'Setup', 
      duration: 'Day 1', 
      tasks: ['Account creation', 'Brand customization', 'Initial configuration'],
      icon: <FiZap />
    },
    { 
      phase: 'Migration', 
      duration: 'Days 2-3', 
      tasks: ['Import client data', 'Set up programs', 'Configure macros'],
      icon: <FiDatabase />
    },
    { 
      phase: 'Testing', 
      duration: 'Days 4-5', 
      tasks: ['Test calculations', 'Client onboarding', 'Staff training'],
      icon: <FiRefreshCw />
    },
    { 
      phase: 'Launch', 
      duration: 'Week 2', 
      tasks: ['Go live', 'Monitor feedback', 'Optimize workflow'],
      icon: <FiTrendingUp />
    }
  ];

  // FAQs
  const faqs = [
    {
      question: "How difficult is it to switch from Excel?",
      answer: "Very easy! We provide step-by-step migration guides and can import your existing client data. Most coaches are fully operational within 3-5 days."
    },
    {
      question: "Will my clients need training?",
      answer: "No! The interface is intuitive and mobile-friendly. Clients love how easy it is compared to downloading and opening Excel files."
    },
    {
      question: "Can I customize the calculations?",
      answer: "Yes! You can adjust all formulas, macro ratios, and calculation methods to match your coaching philosophy."
    },
    {
      question: "What about my existing Excel templates?",
      answer: "We can help you recreate your custom calculations and workflows. Plus, you'll get access to our library of professional templates."
    },
    {
      question: "Is my data secure?",
      answer: "Absolutely. We use bank-level encryption, automatic backups, and comply with all data protection regulations."
    }
  ];

  return (
    <div className="features-comparison">
      {/* Hero Section */}
      <section 
        className="comparison-hero"
        style={{
          backgroundImage: `linear-gradient(rgba(10, 10, 10, 0.3), rgba(10, 10, 10, 0.4)), url(${heroImage})`
        }}
      >
        <div className="hero-background">
          <div className="grid-pattern"></div>
          <div className="gradient-overlay"></div>
        </div>
        <div className="hero-content">
          <h1 className="hero-title">
            <span className="title-line1">Stop Fighting With</span>
            <span className="title-highlight">Excel Spreadsheets</span>
            <span className="title-line3">Start Growing Your</span>
            <span className="title-line4">Coaching Business</span>
          </h1>
          <p className="hero-subtitle">
            See exactly how COACHiFY transforms your workflow and saves you hours every week
          </p>
          <div className="hero-stats">
            <div className="stat">
              <span className="stat-number">73%</span>
              <span className="stat-label">Time Saved</span>
            </div>
            <div className="stat">
              <span className="stat-number">0</span>
              <span className="stat-label">Formula Errors</span>
            </div>
            <div className="stat">
              <span className="stat-number">100%</span>
              <span className="stat-label">Mobile Ready</span>
            </div>
          </div>
        </div>
      </section>

      {/* Pain Points Section */}
      <section className="pain-points-section">
        <div className="container">
          <h2 className="section-title">Your Excel Frustrations, Solved</h2>
          <div className="pain-points-grid">
            {painPoints.map((point, index) => (
              <div key={index} className="pain-point-card">
                <div className="pain-icon">{point.icon}</div>
                <h3>{point.title}</h3>
                <div className="comparison-box">
                  <div className="excel-side">
                    <div className="side-header excel">
                      <FiX /> Excel Reality
                    </div>
                    <p>{point.excel}</p>
                  </div>
                  <div className="divider-arrow">
                    <FiArrowRight />
                  </div>
                  <div className="coachify-side">
                    <div className="side-header coachify">
                      <FiCheck /> COACHiFY Solution
                    </div>
                    <p>{point.coachify}</p>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Feature Comparison Tabs */}
      <section className="comparison-tabs-section">
        <div className="container">
          <div className="tabs-header">
            <button 
              className={`tab ${activeTab === 'features' ? 'active' : ''}`}
              onClick={() => setActiveTab('features')}
            >
              <FiBarChart /> Feature Comparison
            </button>
            <button 
              className={`tab ${activeTab === 'savings' ? 'active' : ''}`}
              onClick={() => setActiveTab('savings')}
            >
              <FiClock /> Time Savings
            </button>
            <button 
              className={`tab ${activeTab === 'roi' ? 'active' : ''}`}
              onClick={() => setActiveTab('roi')}
            >
              <FiDollarSign /> ROI Calculator
            </button>
            <button 
              className={`tab ${activeTab === 'timeline' ? 'active' : ''}`}
              onClick={() => setActiveTab('timeline')}
            >
              <FiTrendingUp /> Implementation
            </button>
          </div>

          {/* Features Tab */}
          {activeTab === 'features' && (
            <div className="tab-content features-tab">
              <h3>Detailed Feature Comparison</h3>
              {features.map((category, idx) => (
                <div key={idx} className="feature-category">
                  <h4>{category.category}</h4>
                  <div className="feature-table">
                    <div className="table-header">
                      <div className="feature-name">Feature</div>
                      <div className="excel-column">Excel</div>
                      <div className="coachify-column">COACHiFY</div>
                    </div>
                    {category.items.map((item, index) => (
                      <div key={index} className="feature-row">
                        <div className="feature-name">{item.feature}</div>
                        <div className={`excel-column status-${item.excelStatus}`}>
                          <span className="status-text">{item.excel}</span>
                          {item.excelStatus === 'none' && <FiX />}
                          {item.excelStatus === 'partial' && <FiAlertCircle />}
                          {item.excelStatus === 'full' && <FiCheck />}
                        </div>
                        <div className={`coachify-column status-${item.coachifyStatus}`}>
                          <span className="status-text">{item.coachify}</span>
                          {item.coachifyStatus === 'full' && <FiCheck />}
                          {item.coachifyStatus === 'partial' && <FiClock />}
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              ))}
            </div>
          )}

          {/* Time Savings Tab */}
          {activeTab === 'savings' && (
            <div className="tab-content savings-tab">
              <h3>Time Savings Breakdown</h3>
              <div className="savings-overview">
                <div className="big-savings">
                  <div className="savings-number">{savings.hoursSavedPerMonth.toFixed(1)}</div>
                  <div className="savings-label">Hours Saved Per Month</div>
                </div>
                <div className="savings-details">
                  <p>Based on {calculatorValues.clients} clients</p>
                  <p className="highlight">That's {(savings.hoursSavedPerMonth / 4).toFixed(1)} hours back every week!</p>
                </div>
              </div>
              
              <div className="task-comparison">
                <h4>Time Per Task Comparison (minutes)</h4>
                {savings.tasksPerClient.map((task, index) => (
                  <div key={index} className="task-row">
                    <div className="task-name">{task.task}</div>
                    <div className="time-bars">
                      <div className="excel-bar" style={{width: `${(task.excelTime / 30) * 100}%`}}>
                        <span>{task.excelTime}m</span>
                      </div>
                      <div className="coachify-bar" style={{width: `${(task.coachifyTime / 30) * 100}%`}}>
                        <span>{task.coachifyTime}m</span>
                      </div>
                    </div>
                    <div className="time-saved">-{task.excelTime - task.coachifyTime}m</div>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* ROI Calculator Tab */}
          {activeTab === 'roi' && (
            <div className="tab-content roi-tab">
              <h3>Calculate Your ROI</h3>
              <div className="roi-calculator">
                <div className="calculator-inputs">
                  <div className="input-group">
                    <label>
                      <FiUsers /> Number of Clients
                    </label>
                    <input 
                      type="number"
                      value={calculatorValues.clients}
                      onChange={(e) => setCalculatorValues({...calculatorValues, clients: parseInt(e.target.value) || 0})}
                    />
                  </div>
                  <div className="input-group">
                    <label>
                      <FiClock /> Hours on Admin/Week
                    </label>
                    <input 
                      type="number"
                      value={calculatorValues.hoursPerWeek}
                      onChange={(e) => setCalculatorValues({...calculatorValues, hoursPerWeek: parseInt(e.target.value) || 0})}
                    />
                  </div>
                  <div className="input-group">
                    <label>
                      <FiDollarSign /> Your Hourly Rate
                    </label>
                    <input 
                      type="number"
                      value={calculatorValues.hourlyRate}
                      onChange={(e) => setCalculatorValues({...calculatorValues, hourlyRate: parseInt(e.target.value) || 0})}
                    />
                  </div>
                </div>
                
                <div className="roi-results">
                  <div className="result-card primary">
                    <h4>Monthly Value of Time Saved</h4>
                    <div className="result-value">${savings.moneySaved.toFixed(0)}</div>
                    <p>That's ${(savings.moneySaved * 12).toFixed(0)} per year!</p>
                  </div>
                  <div className="result-card">
                    <h4>Additional Revenue Potential</h4>
                    <div className="result-value">+{Math.floor(savings.hoursSavedPerMonth / 2)} clients</div>
                    <p>Extra capacity from time saved</p>
                  </div>
                  <div className="result-card">
                    <h4>COACHiFY Cost</h4>
                    <div className="result-value">$97/mo</div>
                    <p>ROI: {((savings.moneySaved / 97) * 100).toFixed(0)}%</p>
                  </div>
                </div>
              </div>
            </div>
          )}

          {/* Implementation Timeline Tab */}
          {activeTab === 'timeline' && (
            <div className="tab-content timeline-tab">
              <h3>Implementation Timeline</h3>
              <p className="timeline-intro">
                Get up and running in less than a week with our proven implementation process
              </p>
              <div className="timeline">
                {timeline.map((phase, index) => (
                  <div key={index} className="timeline-phase">
                    <div className="phase-icon">{phase.icon}</div>
                    <div className="phase-content">
                      <h4>{phase.phase}</h4>
                      <span className="phase-duration">{phase.duration}</span>
                      <ul>
                        {phase.tasks.map((task, idx) => (
                          <li key={idx}>{task}</li>
                        ))}
                      </ul>
                    </div>
                    {index < timeline.length - 1 && <div className="timeline-connector"></div>}
                  </div>
                ))}
              </div>
              <div className="implementation-support">
                <FiShield />
                <div>
                  <h4>Full Support Included</h4>
                  <p>Our team guides you through every step with video tutorials, live chat, and migration assistance</p>
                </div>
              </div>
            </div>
          )}
        </div>
      </section>

      {/* Excel Screenshot Comparison */}
      <section className="screenshot-section">
        <div className="container">
          <h2 className="section-title">See The Difference</h2>
          <div className="screenshot-comparison">
            <div className="excel-screenshot">
              <div className="screenshot-header excel">
                <span>❌ The Old Way: Excel Chaos</span>
              </div>
              <div className="excel-mockup">
                <div className="excel-formula-bar">=IF(B2="Male",10*C2+6.25*D2-5*E2+5,10*C2+6.25*D2-5*E2-161)</div>
                <div className="excel-grid">
                  <div className="excel-errors">
                    <div className="error-cell">#REF!</div>
                    <div className="error-cell">#VALUE!</div>
                    <div className="error-cell">#DIV/0!</div>
                  </div>
                  <div className="excel-mess">Multiple sheets, broken links, version conflicts...</div>
                </div>
              </div>
            </div>
            
            <div className="coachify-screenshot">
              <div className="screenshot-header coachify">
                <span>✅ The New Way: COACHiFY</span>
              </div>
              <div className="coachify-mockup">
                <div className="clean-interface">
                  <div className="stat-cards">
                    <div className="mini-card">
                      <span className="card-value">1,850</span>
                      <span className="card-label">Daily Calories</span>
                    </div>
                    <div className="mini-card">
                      <span className="card-value">140g</span>
                      <span className="card-label">Protein Target</span>
                    </div>
                    <div className="mini-card">
                      <span className="card-value">5 lbs</span>
                      <span className="card-label">Progress</span>
                    </div>
                  </div>
                  <div className="interface-preview">
                    Clean, modern, mobile-friendly interface
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* FAQ Section */}
      <section className="faq-section">
        <div className="container">
          <h2 className="section-title">Common Questions</h2>
          <div className="faq-list">
            {faqs.map((faq, index) => (
              <div key={index} className="faq-item">
                <button 
                  className="faq-question"
                  onClick={() => setExpandedFaq(expandedFaq === index ? null : index)}
                >
                  <span>{faq.question}</span>
                  {expandedFaq === index ? <FiChevronUp /> : <FiChevronDown />}
                </button>
                {expandedFaq === index && (
                  <div className="faq-answer">
                    <p>{faq.answer}</p>
                  </div>
                )}
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="comparison-cta">
        <div className="container">
          <div className="cta-content">
            <h2>Ready to Leave Excel Behind?</h2>
            <p>Join hundreds of coaches who've already made the switch</p>
            <div className="cta-buttons">
              <Link to="/calculator" className="cta-primary">
                Try Demo Now
                <FiArrowRight />
              </Link>
              <button className="cta-secondary">
                <FiUsers />
                See Success Stories
              </button>
            </div>
            <div className="guarantee">
              <FiShield />
              <span>30-day money-back guarantee • No setup fees • Cancel anytime</span>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
};

export default FeaturesComparison;